import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Plus, CheckCircle2, Download, FileText, Building2,
  AlertTriangle, Loader2, Copy, Banknote, Calendar, Users
} from 'lucide-react';

const STATUS_LABEL = {
  draft: { label: 'Utkast', color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' },
  approved: { label: 'Godkänd', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  paid: { label: 'Utbetald', color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
};

const fmtKr = (v) => `${Number(v || 0).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;

const StatusPill = ({ status }) => {
  const s = STATUS_LABEL[status] || STATUS_LABEL.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

// ============================================================================
// LIST VIEW
// ============================================================================

const RunsList = ({ onSelect, onCreate }) => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(null);
  const [expandedEmp, setExpandedEmp] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, cm] = await Promise.all([
          api.listPayrollRuns(),
          api.getCurrentMonthPayroll(),
        ]);
        setRuns(r.data || []);
        setCurrentMonth(cm.data);
      } catch (e) {
        toast.error('Kunde inte hämta löneperioder');
      } finally { setLoading(false); }
    })();
  }, []);

  const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="payroll-runs-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Löneutbetalning
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Automatiserad löneperiod → SEPA-fil till banken + AGI till Skatteverket
          </p>
        </div>
        <Button
          onClick={onCreate}
          className="bg-[#E8613A] hover:bg-[#D14F2A] text-white"
          data-testid="new-payroll-run-btn"
        >
          <Plus size={16} className="mr-1.5" /> Ny löneperiod
        </Button>
      </div>

      {/* Live-widget: löpande ackumulering för innevarande månad */}
      {currentMonth && currentMonth.totals.employee_count > 0 && (
        <div className="bg-gradient-to-br from-[#E8613A]/5 to-transparent border-2 border-[#E8613A]/20 rounded-2xl p-5 mb-6" data-testid="current-month-widget">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#E8613A] font-bold">Denna månad · löpande</p>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {MONTHS[currentMonth.month - 1]} {currentMonth.year}
              </h3>
            </div>
            <button
              onClick={() => setExpandedEmp(!expandedEmp)}
              className="text-xs text-[#E8613A] font-semibold hover:underline"
              data-testid="toggle-cm-details"
            >
              {expandedEmp ? 'Dölj detaljer' : 'Visa per anställd'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Anställda</p>
              <p className="text-2xl font-bold">{currentMonth.totals.employee_count}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Bruttolön</p>
              <p className="text-2xl font-bold">{fmtKr(currentMonth.totals.total_gross)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Nettolön (utbet.)</p>
              <p className="text-2xl font-bold text-[#E8613A]">{fmtKr(currentMonth.totals.total_net)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted-foreground">Till Skatteverket</p>
              <p className="text-2xl font-bold text-muted-foreground">{fmtKr((currentMonth.totals.total_tax || 0) + (currentMonth.totals.total_ag || 0))}</p>
            </div>
          </div>
          {(currentMonth.totals.with_iban_missing > 0 || currentMonth.totals.with_pnr_missing > 0) && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle size={12} />
              {currentMonth.totals.with_iban_missing > 0 && `${currentMonth.totals.with_iban_missing} anställda saknar IBAN`}
              {currentMonth.totals.with_iban_missing > 0 && currentMonth.totals.with_pnr_missing > 0 && ' · '}
              {currentMonth.totals.with_pnr_missing > 0 && `${currentMonth.totals.with_pnr_missing} saknar personnummer`}
            </div>
          )}
          {expandedEmp && (
            <div className="mt-4 pt-4 border-t border-[#E8613A]/20 space-y-2" data-testid="cm-employee-list">
              {(currentMonth.employees || []).filter(e => e.gross_estimate > 0).map(e => (
                <div key={e.employee_id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium">{e.employee_name}</span>
                    {e.is_consultant && <span className="ml-2 text-[10px] uppercase text-purple-700 font-bold">Konsult</span>}
                    {e.is_fixed_monthly && <span className="ml-2 text-[10px] uppercase text-blue-700 font-bold">Månadslön</span>}
                    <span className="text-xs text-muted-foreground ml-2">{e.hours}h</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-[#E8613A]">{fmtKr(e.net_estimate)}</span>
                    <span className="text-xs text-muted-foreground ml-2">/ {fmtKr(e.gross_estimate)} brutto</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E8613A]" /></div>
      ) : runs.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center">
          <Banknote size={40} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-lg font-semibold mb-1">Inga löneperioder ännu</p>
          <p className="text-sm text-muted-foreground mb-4">
            Skapa din första löneperiod så räknar Operio alla anställda automatiskt.
            <br />Från och med nästa månad skapas löneperioden automatiskt den 20:e.
          </p>
          <Button onClick={onCreate} className="bg-[#E8613A] hover:bg-[#D14F2A] text-white">
            <Plus size={16} className="mr-1.5" /> Skapa löneperiod
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {runs.map(run => {
            const totalNet = (run.employees || []).reduce((s, e) => s + (e.net || 0), 0);
            return (
              <div
                key={run.id}
                onClick={() => onSelect(run.id)}
                className="bg-card border border-border rounded-xl p-5 hover:border-[#E8613A]/40 cursor-pointer transition-all"
                data-testid={`payroll-run-${run.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                        {run.period_start} — {run.period_end}
                      </h3>
                      <StatusPill status={run.status} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                      <span className="flex items-center gap-1"><Users size={11} /> {(run.employees || []).length} anställda</span>
                      <span className="flex items-center gap-1"><Calendar size={11} /> Utbet. {run.execution_date}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Total nettolön</p>
                    <p className="text-xl font-bold text-[#E8613A]">{fmtKr(totalNet)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CREATE MODAL
// ============================================================================

const CreateRunModal = ({ onClose, onCreated }) => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  const [start, setStart] = useState(first);
  const [end, setEnd] = useState(last);
  const [execDate, setExecDate] = useState(last);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      const r = await api.createPayrollRun({ period_start: start, period_end: end, execution_date: execDate });
      toast.success('Löneperiod skapad');
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skapa löneperiod');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Ny löneperiod
        </h3>
        <div className="space-y-4">
          <div>
            <Label>Från datum</Label>
            <Input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="period-start" />
          </div>
          <div>
            <Label>Till datum</Label>
            <Input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="period-end" />
          </div>
          <div>
            <Label>Utbetalningsdatum (i banken)</Label>
            <Input type="date" value={execDate} onChange={e => setExecDate(e.target.value)} data-testid="exec-date" />
            <p className="text-xs text-muted-foreground mt-1">Datum då banken kör SEPA-överföringen</p>
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1">Avbryt</Button>
          <Button onClick={create} disabled={saving} className="flex-1 bg-[#E8613A] hover:bg-[#D14F2A] text-white" data-testid="create-run-submit">
            {saving ? <Loader2 size={16} className="animate-spin" /> : 'Skapa'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DETAIL VIEW
// ============================================================================

const RunDetail = ({ runId, onBack }) => {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const reload = async () => {
    try {
      const r = await api.getPayrollRun(runId);
      setRun(r.data);
    } catch (e) {
      toast.error('Kunde inte ladda löneperiod');
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [runId]);

  const approve = async () => {
    setProcessing(true);
    try {
      await api.approvePayrollRun(runId);
      toast.success('Löneperiod godkänd. Ladda ner filerna nedan.');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte godkänna');
    } finally { setProcessing(false); }
  };

  const markPaid = async () => {
    if (!window.confirm('Bekräfta att du:\n\n1. Laddat upp SEPA-filen i banken (nettolönerna utbetalas)\n2. Laddat upp AGI-filen på Skatteverket\n3. Betalat skatten till Bankgiro 5050-1055 med OCR\n\nLönespec-PDF genereras och mejlas ut till alla anställda. Går inte att ångra.')) return;
    setProcessing(true);
    try {
      const r = await api.markPayrollRunPaid(runId);
      toast.success(r.data.message);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte markera som utbetald');
    } finally { setProcessing(false); }
  };

  const dlSepa = async () => {
    try {
      const r = await api.downloadSepa(runId);
      downloadBlob(new Blob([r.data]), `sepa-lon-${run.period_yyyymm}.xml`);
      toast.success('SEPA-fil nedladdad');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ladda ner SEPA');
    }
  };
  const dlAgi = async () => {
    try {
      const r = await api.downloadAgi(runId);
      downloadBlob(new Blob([r.data]), `agi-${run.period_yyyymm}.xml`);
      toast.success('AGI-fil nedladdad');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ladda ner AGI');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E8613A]" /></div>;
  if (!run) return null;

  const s = run.summary || {};
  const isApproved = run.status === 'approved';
  const isPaid = run.status === 'paid';
  const canApprove = run.status === 'draft';

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="payroll-run-detail">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-[#1A1A1A] mb-4 flex items-center gap-1" data-testid="back-to-runs">
        <ArrowLeft size={14} /> Tillbaka
      </button>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {run.period_start} — {run.period_end}
            </h1>
            <StatusPill status={run.status} />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Utbetalning {run.execution_date}</p>
        </div>
      </div>

      {/* Anställda-tabell */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden mb-6">
        <div className="p-4 border-b border-border">
          <h2 className="font-bold flex items-center gap-2">
            <Users size={16} className="text-[#E8613A]" />
            Anställda ({(run.employees || []).length})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2.5">Namn</th>
                <th className="text-right px-4 py-2.5">Timmar</th>
                <th className="text-right px-4 py-2.5">Brutto</th>
                <th className="text-right px-4 py-2.5">Skatt</th>
                <th className="text-right px-4 py-2.5">Netto</th>
                <th className="text-right px-4 py-2.5">AG-avgift</th>
              </tr>
            </thead>
            <tbody>
              {(run.employees || []).map(e => (
                <tr key={e.employee_id} className="border-t border-border/50" data-testid={`emp-row-${e.employee_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{e.name}</div>
                    {e.is_consultant && <span className="text-[10px] uppercase tracking-wide text-purple-700 font-semibold">Konsult (F-skatt)</span>}
                    {!e.iban && !e.is_consultant && (
                      <span className="text-[10px] text-red-600 flex items-center gap-1"><AlertTriangle size={10} /> Saknar bankkonto</span>
                    )}
                  </td>
                  <td className="text-right px-4 py-3">{e.regular_hours} h {e.overtime_hours > 0 && `+ ${e.overtime_hours} övt`}</td>
                  <td className="text-right px-4 py-3">{fmtKr(e.gross)}</td>
                  <td className="text-right px-4 py-3 text-muted-foreground">{fmtKr(e.tax)}</td>
                  <td className="text-right px-4 py-3 font-bold text-[#E8613A]">{fmtKr(e.net)}</td>
                  <td className="text-right px-4 py-3 text-muted-foreground text-xs">{fmtKr(e.ag_avgift)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/20 font-bold">
              <tr className="border-t-2 border-border">
                <td className="px-4 py-3">Total</td>
                <td></td>
                <td className="text-right px-4 py-3">{fmtKr(s.total_gross_salary)}</td>
                <td className="text-right px-4 py-3">{fmtKr(s.total_income_tax)}</td>
                <td className="text-right px-4 py-3 text-[#E8613A]">{fmtKr(s.total_net_salary)}</td>
                <td className="text-right px-4 py-3">{fmtKr(s.total_employer_contribution)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Godkänn / Filer / Markera utbetald */}
      {canApprove && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-amber-900">Godkänn löneperioden</h3>
              <p className="text-sm text-amber-800 mt-1">
                Granska tabellen ovan. När du godkänner låses beloppen och du får ladda ner
                utbetalnings-filerna för bank + Skatteverket.
              </p>
            </div>
            <Button onClick={approve} disabled={processing} className="bg-[#E8613A] hover:bg-[#D14F2A] text-white" data-testid="approve-run-btn">
              {processing ? <Loader2 size={16} className="animate-spin" /> : <>
                <CheckCircle2 size={16} className="mr-1.5" /> Godkänn
              </>}
            </Button>
          </div>
        </div>
      )}

      {(isApproved || isPaid) && (
        <>
          {/* 3-stegs utbetalnings-guide */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden mb-6">
            <div className="p-4 border-b border-border bg-[#E8613A]/5">
              <h2 className="font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                <FileText size={16} className="text-[#E8613A]" /> Utbetalnings-guide — 3 steg
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Ladda upp filerna hos banken och Skatteverket, betala skatten, och markera perioden utbetald.
              </p>
            </div>

            {/* STEG 1: SEPA */}
            <div className="p-4 border-b border-border">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E8613A] text-white flex items-center justify-center font-bold shrink-0">1</div>
                <div className="flex-1">
                  <p className="font-semibold">Utbetala nettolön till anställda</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Ladda ner SEPA-filen och ladda upp i din banks företagstjänst (Swedbank, Handelsbanken, SEB, Nordea, m.fl.).
                    Alla {(run.employees || []).length} anställda får {fmtKr(s.total_net_salary)} totalt.
                  </p>
                  <Button onClick={dlSepa} variant="outline" className="mt-3" data-testid="download-sepa">
                    <Download size={14} className="mr-1.5" /> Ladda ner SEPA XML
                  </Button>
                </div>
              </div>
            </div>

            {/* STEG 2: AGI */}
            <div className="p-4 border-b border-border">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E8613A] text-white flex items-center justify-center font-bold shrink-0">2</div>
                <div className="flex-1">
                  <p className="font-semibold">Rapportera till Skatteverket</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Ladda ner AGI-filen och ladda upp på <a href="https://skatteverket.se" target="_blank" rel="noreferrer" className="text-[#E8613A] underline">Skatteverkets e-tjänst</a> → &quot;Arbetsgivardeklaration&quot;. Godkänn med BankID.
                  </p>
                  <Button onClick={dlAgi} variant="outline" className="mt-3" data-testid="download-agi">
                    <Download size={14} className="mr-1.5" /> Ladda ner AGI XML
                  </Button>
                </div>
              </div>
            </div>

            {/* STEG 3: OCR-betalning */}
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E8613A] text-white flex items-center justify-center font-bold shrink-0">3</div>
                <div className="flex-1">
                  <p className="font-semibold">Betala skatten till Skatteverkets skattekonto</p>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                    Preliminärskatt + arbetsgivaravgifter. Betalas via internetbanken som en Bankgiro-betalning.
                  </p>
                  <div className="bg-muted/30 rounded-lg p-4 grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">Belopp</p>
                      <p className="font-bold text-lg text-[#E8613A]">{fmtKr(s.total_to_skatteverket)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">Bankgiro</p>
                      <p className="font-bold text-lg font-mono">{s.skatteverket_bankgiro}</p>
                      <button onClick={() => { navigator.clipboard.writeText(s.skatteverket_bankgiro); toast.success('Kopierat!'); }} className="text-[10px] text-[#E8613A] flex items-center gap-1 mt-0.5" data-testid="copy-bg">
                        <Copy size={9} /> Kopiera
                      </button>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">OCR</p>
                      <p className="font-bold text-sm font-mono break-all">{s.skatteverket_ocr}</p>
                      <button onClick={() => { navigator.clipboard.writeText(s.skatteverket_ocr); toast.success('Kopierat!'); }} className="text-[10px] text-[#E8613A] flex items-center gap-1 mt-0.5" data-testid="copy-ocr">
                        <Copy size={9} /> Kopiera
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Markera utbetald */}
          {isApproved && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-emerald-900">Klar med alla 3 steg?</h3>
                <p className="text-sm text-emerald-800 mt-0.5">
                  Markera perioden som utbetald. Lönespec-PDF mejlas till alla anställda och sparas i worker-appen.
                </p>
              </div>
              <Button onClick={markPaid} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mark-paid-btn">
                {processing ? <Loader2 size={16} className="animate-spin" /> : <>
                  <CheckCircle2 size={16} className="mr-1.5" /> Markera utbetald
                </>}
              </Button>
            </div>
          )}

          {isPaid && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3">
              <CheckCircle2 size={24} className="text-emerald-600" />
              <div>
                <h3 className="font-bold text-emerald-900">Utbetald</h3>
                <p className="text-sm text-emerald-800">
                  Lönespec skickad till alla anställda. Perioden är klar.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// MAIN
// ============================================================================

const PayrollRunsPage = () => {
  const [view, setView] = useState('list');   // 'list' | 'detail'
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  if (view === 'detail' && selectedId) {
    return <RunDetail runId={selectedId} onBack={() => setView('list')} />;
  }

  return (
    <>
      <RunsList
        onSelect={(id) => { setSelectedId(id); setView('detail'); }}
        onCreate={() => setShowCreate(true)}
      />
      {showCreate && (
        <CreateRunModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); setSelectedId(id); setView('detail'); }}
        />
      )}
    </>
  );
};

export default PayrollRunsPage;
